<template>
  <div class="pe-page">
    <div class="pe-vista-bar">
      <div class="pe-field pe-field--date">
        <label>Periodo</label>
        <input v-model="periodoMes" type="month" @change="cargar">
      </div>
      <div v-if="config.requiereSala" class="pe-field">
        <label>Sala</label>
        <select v-model="sala" @change="cargar">
          <option v-for="n in salasOpciones" :key="n" :value="n">Sala {{ n }}</option>
        </select>
      </div>
      <div class="pe-vista-meta">
        <strong>{{ config.titulo }}</strong>
        <span>{{ mesAnioLabel }}</span>
      </div>
      <div class="pe-header__actions">
        <button
          type="button"
          class="pe-icon-btn"
          title="Restablecer"
          :disabled="cargando || guardando"
          @click="restablecer"
        >
          <v-icon size="20" color="#334155">mdi-refresh</v-icon>
        </button>
        <button
          type="button"
          class="pe-btn pe-btn--primary"
          :disabled="guardando || cargando || (config.requiereSala && !sala.trim())"
          @click="guardar"
        >
          <v-icon size="18" color="white">mdi-content-save-outline</v-icon>
          {{ guardando ? 'Guardando…' : 'Guardar Registro' }}
        </button>
      </div>
    </div>

    <v-alert
      v-if="config.requiereSala && !sala.trim()"
      type="info"
      dense
      outlined
      class="mb-3"
    >
      Seleccione la sala para cargar y registrar el abastecimiento.
    </v-alert>

    <v-alert
      v-if="edicionLibre"
      type="warning"
      dense
      outlined
      class="mb-3"
    >
      Modo edición libre: puede registrar cualquier día, editar nombres y agregar o eliminar campos.
    </v-alert>

    <KardexGrid
      v-if="config && (!config.requiereSala || sala.trim())"
      :config="config"
      :mes-label="mesAnioLabel"
      :anio="anio"
      :mes="mes"
      :sala-label="config.requiereSala ? sala : ''"
      :valores="valores"
      :etiquetas="etiquetas"
      :estructura="estructura"
      :edicion-libre="edicionLibre"
      @celda="onCelda"
      @etiqueta="onEtiqueta"
      @agregar-item="onAgregarItem"
      @eliminar-item="onEliminarItem"
      @agregar-sala="onAgregarSala"
      @eliminar-sala="onEliminarSala"
    />
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import KardexGrid from './KardexGrid.vue'
import {
  configKardex,
  MESES_NOMBRE,
  buildDiasMes,
  buildDiasRegistro,
  blankEstructura,
  normalizarEstructura,
  aplicarEstructura,
  idsFilasConfig,
  nuevoIdCampo,
} from './catalogos'
import { apiGetAutenticado, apiPostAutenticado } from '@/services/apiService/apiMethodService'

export default {
  name: 'PeKardexPage',
  components: { KardexGrid },
  props: {
    tipo: { type: String, default: '' },
    edicionLibreProp: { type: Boolean, default: false },
  },
  data() {
    return {
      periodoMes: '',
      sala: '1',
      salasOpciones: ['1', '2', '3', '4'],
      valores: {},
      etiquetas: { items: {}, categorias: {}, salas: {} },
      estructura: blankEstructura(),
      cargando: false,
      guardando: false,
      registroId: null,
    }
  },
  computed: {
    tipoActivo() {
      return this.tipo || (this.$route.meta && this.$route.meta.kardexTipo) || ''
    },
    edicionLibre() {
      if (this.edicionLibreProp) return true
      return !!(this.$route.meta && this.$route.meta.edicionLibre)
    },
    configBase() {
      return configKardex(this.tipoActivo) || {
        titulo: 'Kardex',
        subtitulo: '',
        requiereSala: false,
        modo: 'items',
        categorias: [],
      }
    },
    config() {
      return this.configBase
    },
    configVista() {
      return aplicarEstructura(this.configBase, this.estructura)
    },
    anio() {
      if (!this.periodoMes) return new Date().getFullYear()
      return Number(this.periodoMes.split('-')[0])
    },
    mes() {
      if (!this.periodoMes) return new Date().getMonth() + 1
      return Number(this.periodoMes.split('-')[1])
    },
    mesAnioLabel() {
      return `${MESES_NOMBRE[this.mes] || ''} ${this.anio}`.trim()
    },
    idsFilas() {
      return idsFilasConfig(this.configVista)
    },
    totalGeneral() {
      let sum = 0
      const dias = buildDiasRegistro(this.anio, this.mes)
      this.idsFilas.forEach((id) => {
        const fila = this.valores[id] || {}
        dias.forEach((d) => {
          sum += Number(fila[String(d)] || 0)
        })
      })
      return sum
    },
  },
  created() {
    const now = new Date()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    this.periodoMes = `${now.getFullYear()}-${m}`
    this.initValores()
    this.cargar()
  },
  watch: {
    tipoActivo() {
      this.initValores()
      if (this.config.requiereSala && !this.salasOpciones.includes(this.sala)) {
        this.sala = '1'
      }
      this.cargar()
    },
  },
  methods: {
    blankEtiquetas() {
      return { items: {}, categorias: {}, salas: {} }
    },
    normalizarEtiquetas(raw) {
      const base = this.blankEtiquetas()
      if (!raw || typeof raw !== 'object') return base
      ;['items', 'categorias', 'salas'].forEach((k) => {
        if (raw[k] && typeof raw[k] === 'object') base[k] = { ...raw[k] }
      })
      return base
    },
    asegurarFilaValores(id) {
      if (!id) return
      if (!this.valores[id]) this.$set(this.valores, id, {})
      const dias = buildDiasMes(this.anio, this.mes)
      dias.forEach((d) => {
        if (this.valores[id][String(d)] === undefined) {
          this.$set(this.valores[id], String(d), 0)
        }
      })
    },
    initValores() {
      const next = {}
      const dias = buildDiasMes(this.anio, this.mes)
      this.idsFilas.forEach((id) => {
        next[id] = {}
        dias.forEach((d) => {
          next[id][String(d)] = 0
        })
      })
      this.valores = next
      this.etiquetas = this.blankEtiquetas()
      this.estructura = blankEstructura()
      this.registroId = null
    },
    async cargar() {
      if (!this.tipoActivo) return
      if (this.config.requiereSala && !String(this.sala || '').trim()) {
        this.initValores()
        return
      }
      this.cargando = true
      try {
        const sala = this.config.requiereSala ? String(this.sala).trim() : ''
        const qs = [
          `tipo=${encodeURIComponent(this.tipoActivo)}`,
          `anio=${this.anio}`,
          `mes=${this.mes}`,
          `sala=${encodeURIComponent(sala)}`,
        ].join('&')
        const res = await apiGetAutenticado(`/pe_kardex/buscar/?${qs}`)
        this.etiquetas = this.blankEtiquetas()
        this.estructura = blankEstructura()
        if (res && res.existe) {
          this.registroId = res.id
          const resp = res.responsables || {}
          this.etiquetas = this.normalizarEtiquetas(resp.etiquetas)
          this.estructura = normalizarEstructura(resp.estructura)
        } else {
          this.registroId = null
        }
        const next = {}
        const dias = buildDiasMes(this.anio, this.mes)
        idsFilasConfig(aplicarEstructura(this.configBase, this.estructura)).forEach((id) => {
          next[id] = {}
          dias.forEach((d) => {
            next[id][String(d)] = 0
          })
        })
        this.valores = next
        if (res && res.existe) {
          const vals = res.valores || {}
          Object.keys(vals).forEach((id) => {
            if (!this.valores[id]) this.$set(this.valores, id, {})
            const fila = vals[id] || {}
            Object.keys(fila).forEach((dia) => {
              this.$set(this.valores[id], String(dia), Number(fila[dia]) || 0)
            })
          })
        }
      } catch (e) {
        console.error(e)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar el registro.',
        })
      } finally {
        this.cargando = false
      }
    },
    onCelda({ itemId, dia, raw }) {
      if (!this.valores[itemId]) this.$set(this.valores, itemId, {})
      const n = raw === '' ? 0 : Number(raw)
      this.$set(this.valores[itemId], String(dia), Number.isFinite(n) ? n : 0)
    },
    onEtiqueta({ grupo, id, nombre }) {
      if (!grupo || id == null) return
      if (!this.etiquetas[grupo]) this.$set(this.etiquetas, grupo, {})
      this.$set(this.etiquetas[grupo], id, nombre)
      // Si es un extra, sincronizar nombre en estructura
      if (grupo === 'items') {
        const extra = (this.estructura.itemsExtra || []).find(x => String(x.id) === String(id))
        if (extra) this.$set(extra, 'nombre', nombre)
      }
      if (grupo === 'salas') {
        const extra = (this.estructura.salasExtra || []).find(x => String(x.id) === String(id))
        if (extra) this.$set(extra, 'nombre', nombre)
      }
      if (grupo === 'categorias') {
        const extra = (this.estructura.categoriasExtra || []).find(x => String(x.id) === String(id))
        if (extra) this.$set(extra, 'nombre', nombre)
      }
    },
    onAgregarItem({ categoriaId } = {}) {
      if (!this.edicionLibre) return
      const id = nuevoIdCampo('item')
      const cats = (this.configVista.categorias || [])
      const catId = categoriaId || (cats.length ? cats[cats.length - 1].id : '')
      if (!this.estructura.itemsExtra) this.$set(this.estructura, 'itemsExtra', [])
      this.estructura.itemsExtra.push({
        id,
        nombre: 'NUEVO CAMPO',
        categoriaId: catId ? String(catId) : '',
      })
      this.$set(this.etiquetas.items, id, 'NUEVO CAMPO')
      this.asegurarFilaValores(id)
      this.estructura = normalizarEstructura(this.estructura)
    },
    onEliminarItem({ id }) {
      if (!this.edicionLibre || id == null) return
      const sid = String(id)
      const extras = this.estructura.itemsExtra || []
      const idx = extras.findIndex(x => String(x.id) === sid)
      if (idx >= 0) {
        extras.splice(idx, 1)
      } else if (!(this.estructura.itemsEliminados || []).includes(sid)) {
        if (!this.estructura.itemsEliminados) this.$set(this.estructura, 'itemsEliminados', [])
        this.estructura.itemsEliminados.push(sid)
      }
      if (this.etiquetas.items && this.etiquetas.items[sid] !== undefined) {
        this.$delete(this.etiquetas.items, sid)
      }
      if (this.valores[sid]) this.$delete(this.valores, sid)
      this.estructura = normalizarEstructura(this.estructura)
    },
    onAgregarSala() {
      if (!this.edicionLibre) return
      const id = nuevoIdCampo('sala')
      if (!this.estructura.salasExtra) this.$set(this.estructura, 'salasExtra', [])
      this.estructura.salasExtra.push({ id, nombre: 'NUEVA SALA' })
      this.$set(this.etiquetas.salas, id, 'NUEVA SALA')
      this.asegurarFilaValores(id)
      this.estructura = normalizarEstructura(this.estructura)
    },
    onEliminarSala({ id }) {
      if (!this.edicionLibre || id == null) return
      const sid = String(id)
      const extras = this.estructura.salasExtra || []
      const idx = extras.findIndex(x => String(x.id) === sid)
      if (idx >= 0) {
        extras.splice(idx, 1)
      } else if (!(this.estructura.salasEliminadas || []).includes(sid)) {
        if (!this.estructura.salasEliminadas) this.$set(this.estructura, 'salasEliminadas', [])
        this.estructura.salasEliminadas.push(sid)
      }
      if (this.etiquetas.salas && this.etiquetas.salas[sid] !== undefined) {
        this.$delete(this.etiquetas.salas, sid)
      }
      if (this.valores[sid]) this.$delete(this.valores, sid)
      this.estructura = normalizarEstructura(this.estructura)
    },
    restablecer() {
      Swal.fire({
        icon: 'question',
        title: '¿Restablecer?',
        text: 'Se limpiarán los valores no guardados de esta vista.',
        showCancelButton: true,
        confirmButtonText: 'Sí, limpiar',
        cancelButtonText: 'Cancelar',
      }).then((r) => {
        if (r.isConfirmed) {
          this.initValores()
          this.cargar()
        }
      })
    },
    async guardar() {
      if (this.config.requiereSala && !String(this.sala || '').trim()) {
        Swal.fire({ icon: 'info', title: 'Sala requerida', text: 'Indique la sala.' })
        return
      }
      this.guardando = true
      try {
        const payload = {
          tipo: this.tipoActivo,
          anio: this.anio,
          mes: this.mes,
          sala: this.config.requiereSala ? String(this.sala).trim() : '',
          valores: this.valores,
          responsables: {
            etiquetas: this.etiquetas,
            estructura: normalizarEstructura(this.estructura),
          },
          total: this.totalGeneral,
          usuario_id: sessionStorage.getItem('idUsuario'),
          usuario_nombre: sessionStorage.getItem('nombre') || sessionStorage.getItem('usuario') || '',
        }
        const res = await apiPostAutenticado('/pe_kardex/guardar/', payload)
        if (res && res.id) this.registroId = res.id
        Swal.fire({
          icon: 'success',
          title: 'Guardado',
          text: 'El registro se guardó correctamente.',
          timer: 1600,
          showConfirmButton: false,
        })
      } catch (e) {
        console.error(e)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: (e && e.response && e.response.data && e.response.data.detail)
            || 'No se pudo guardar el registro.',
        })
      } finally {
        this.guardando = false
      }
    },
  },
}
</script>

<style scoped>
.pe-page {
  --navy: #0b3d78;
  --line: #e4e8ee;
  --muted: #64748b;
}

.pe-vista-bar {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 12px;
}

.pe-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 120px;
}

.pe-field label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #7b8794;
  text-transform: uppercase;
}

.pe-field input,
.pe-field select {
  height: 40px;
  border: 1px solid #d7dee7;
  border-radius: 8px;
  padding: 0 0.75rem;
  font-size: 0.9rem;
  color: #1e293b;
  background: #fff;
  outline: none;
}

.pe-field input:focus,
.pe-field select:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.35);
}

.pe-vista-meta {
  display: flex;
  flex-direction: column;
  margin-left: auto;
  text-align: right;
  min-width: 160px;
}

.pe-vista-meta strong {
  font-size: 0.82rem;
  color: #0f172a;
  max-width: 320px;
  line-height: 1.25;
}

.pe-vista-meta span {
  font-size: 0.78rem;
  color: var(--muted);
}

.pe-header__actions {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.pe-icon-btn,
.pe-btn {
  border: 1px solid #d7dee7;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: #334155;
}

.pe-icon-btn {
  width: 40px;
  height: 40px;
}

.pe-btn {
  height: 40px;
  padding: 0 0.95rem;
}

.pe-btn--primary {
  background: var(--navy);
  border-color: var(--navy);
  color: #fff;
}

.pe-btn--primary:disabled,
.pe-icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media (max-width: 960px) {
  .pe-vista-meta {
    margin-left: 0;
    text-align: left;
    width: 100%;
  }
}
</style>
